import { ReactComponent as InfoIcon } from 'assett/icons/info_icon.svg';
import ButtonDeleteClaim from 'components/commons/Dialogs/deleteClaim/ButtonDeleteClaim';
import { submitClaim } from 'feature/claims/claimSlice';
import { DELETE_CLAIM_REDIRECT_TO } from 'feature/hooks/deleteClaimSlice';
import useDialogConfirm from 'hooks/DialogConfirm.hook';
import useDialogDeleteClaim from 'hooks/DialogDeleteClaim.hook';
import useDialogSubmitClaim from 'hooks/DialogSubmitClaim.hook';
import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { Button, Checkbox, IconButton, Stack, Typography } from 'styles';
import { IClaim } from 'types/Claim';
import { DOCUMENTATION_INFO, SUBMIT_LATER } from 'types/dialogtypes';
import { PageProps } from 'types/newClaimPageType';
import { DocType } from 'types/Upload';
import { useTranslation } from 'utils/translations';
import Documentation from './components/Documentation/Documentation';
import DocumentationLinks from './components/Documentation/DocumentationLinks';
import PriceEstimate from './components/PriceEstimate';

const Page2: FC<PageProps> = props => {
	const dispatch = useDispatch<AppDispatch>();
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const { formData, setFormData, submitPage, isLastPage } = props;
	const dialogConfirm = useDialogConfirm();
	const dialogDeleteClaim = useDialogDeleteClaim();
	const dialogSubmitClaim = useDialogSubmitClaim();
	const { statusCalculation, claimData } = useSelector(
		(store: RootState) => store.claim,
	);

	const docList: DocType[] = ['prescription', 'receipt', 'other'];

	const handleClickInfo = () => {
		dialogConfirm.open(DOCUMENTATION_INFO);
	};

	const handleClickDeleteClaim = () => {
		const payload = {
			externalClaimId: formData.externalClaimId,
			claimUUID: claimData.claimUUID || '',
			redirectTo: DELETE_CLAIM_REDIRECT_TO.DETAILS,
		};

		dialogDeleteClaim.open(payload);
	};

	const handleClickSubmitClaim = () => {
		if (claimData.status.id === 18) {
			const payload = {
				externalClaimId: formData.externalClaimId,
				claimUUID: claimData.claimUUID || '',
			};

			dialogSubmitClaim.open(payload);
		} else {
			dispatch(submitClaim(formData));
		}
	};

	return (
		<Stack gap="60px">
			<PriceEstimate
				formData={formData}
				setFormData={setFormData}
				status={statusCalculation}
			/>
			{['afterCalculation', 'onEdit'].includes(statusCalculation) ? (
				<Stack gap="40px">
					<Stack gap="30px">
						<Stack direction={'row'} alignItems={'center'} gap={'10px'}>
							<Typography variant="title6">
								{tClientLabels('newClaim.selectedPage.sectionTitile.uploadDoc')}
							</Typography>
							<IconButton
								onClick={handleClickInfo}
								disableFocusRipple
								disableRipple
								disableTouchRipple
								aria-label={`${tClientLabels('dialog.confirm.prescriptionInfo.title')}`}
							>
								<InfoIcon width={24} height={24} />
							</IconButton>
						</Stack>
						<Stack gap={'5px'}>
							<DocumentationLinks docList={docList} formData={formData} />
							<Documentation docList={docList} formData={formData} />
						</Stack>
					</Stack>
					<Stack gap="20px" mt={'-36px'}>
						<Typography
							variant="caption"
							style={{ whiteSpace: 'pre-line', fontSize: '12px' }}
						>
							{tClientLabels('newClaim.upload.warning')}
						</Typography>
						<Checkbox
							sx={{ left: '-9px' }}
							label={tClientLabels('newClaim.upload.uploadConsent')}
							checked={formData.uploadConsent || false}
							onChange={(_, value) => {
								setFormData((prev: IClaim) => ({
									...prev,
									uploadConsent: value,
								}));
							}}
						/>
					</Stack>
					<Stack alignItems="start" gap="20px" direction={'row'}>
						{[1, 16].includes(claimData.status.id) && claimData.claimUUID && (
							<ButtonDeleteClaim
								iconButton={false}
								onClickCallback={handleClickDeleteClaim}
							/>
						)}
						<Button
							variant="outlined"
							onClick={() => {
								dialogConfirm.open(SUBMIT_LATER);
							}}
						>
							{tClientLabels('newClaim.saveFinishLater')}
						</Button>
						<Button
							disabled={
								!formData.uploadConsent ||
								['beforeCalculation', 'onEdit'].includes(statusCalculation) ||
								!formData.attachments?.prescription?.uuid ||
								!formData.attachments?.receipt?.uuid
							}
							variant="contained"
							onClick={() => {
								isLastPage ? handleClickSubmitClaim() : submitPage!();
							}}
						>
							{tClientLabels(
								isLastPage ? 'newClaim.submitClaim' : 'newClaim.selectedPage.proceed',
							)}
						</Button>
					</Stack>
				</Stack>
			) : null}
		</Stack>
	);
};

export default Page2;
